import { type ReactNode } from 'react';
import { NavigationItem } from '../NavigationItem/NavigationItem';
import { Icon } from '../Icon/Icon';
import styles from './Header.module.scss';

export interface HeaderNavigationItem {
  iconName: string;
  label: string;
  isActive?: boolean;
  badge?: number;
  onClick?: () => void;
  href?: string;
  target?: string;
  rel?: string;
  disabled?: boolean;
  'data-testid': string;
}

export interface HeaderProps {
  /** Logo element — e.g. a router Link wrapping an Icon */
  logo: ReactNode;
  /** Top-bar navigation items rendered via NavigationItem */
  navigationItems?: HeaderNavigationItem[];
  /** Accessible label for the <nav> landmark */
  navAriaLabel?: string;
  /** User profile section (avatar + name) */
  userSection?: ReactNode;
  /** Language picker control (e.g. Dropdown) */
  languageControl?: ReactNode;
  /** Theme toggle control (e.g. Dropdown) */
  themeControl?: ReactNode;
  /** Logout click handler */
  onLogout?: () => void;
  /** Visible label next to the logout icon */
  logoutLabel?: string;
  /** Extra content placed in the actions area (before logout) */
  children?: ReactNode;
  /**
   * Hamburger button rendered before the logo on mobile (< 1024 px).
   * Hidden on desktop via --res-mobile-sidebar-btn-display CSS variable.
   */
  mobileSidebarButton?: ReactNode;
  /**
   * Button that opens the collapsed header-nav on mobile.
   * Hidden on desktop via --res-mobile-nav-btn-display CSS variable.
   */
  mobileNavButton?: ReactNode;
  'data-testid'?: string;
}

export const Header = ({
  logo,
  navigationItems = [],
  navAriaLabel = 'Top navigation',
  userSection,
  languageControl,
  themeControl,
  onLogout,
  logoutLabel,
  children,
  mobileSidebarButton,
  mobileNavButton,
  'data-testid': dataTestid = 'header',
}: HeaderProps) => {
  return (
    <header
      className={styles['header']}
      data-testid={dataTestid}
      data-theme="dark"
    >
      {mobileSidebarButton && (
        <div className={styles['header-mobile-sidebar-btn']}>
          {mobileSidebarButton}
        </div>
      )}

      <div className={styles['header-logo']}>{logo}</div>

      {navigationItems.length > 0 && (
        <nav className={styles['header-nav']} aria-label={navAriaLabel}>
          {navigationItems.map((item) => (
            <NavigationItem
              key={item['data-testid']}
              iconName={item.iconName}
              label={item.label}
              isActive={item.isActive}
              badge={item.badge}
              onClick={item.onClick}
              href={item.href}
              target={item.target}
              rel={item.rel}
              disabled={item.disabled}
              data-testid={item['data-testid']}
            />
          ))}
        </nav>
      )}

      <div
        className={styles['header-actions']}
        role="toolbar"
        aria-label="Akce záhlaví"
      >
        {mobileNavButton && (
          <div className={styles['header-mobile-nav-btn']}>
            {mobileNavButton}
          </div>
        )}

        {userSection && (
          <div className={styles['header-user']}>{userSection}</div>
        )}

        {languageControl && (
          <div className={styles['header-control']}>{languageControl}</div>
        )}

        {themeControl && (
          <div className={styles['header-control']}>{themeControl}</div>
        )}

        {children}

        {onLogout && (
          <button
            type="button"
            className={styles['header-logout']}
            onClick={onLogout}
            aria-label={logoutLabel}
            data-testid="header-logout"
          >
            <Icon name="exit" size={24} aria-hidden="true" />
            {logoutLabel && (
              <span className={styles['header-logout-label']}>
                {logoutLabel}
              </span>
            )}
          </button>
        )}
      </div>
    </header>
  );
};
